import { apiV2 } from 'api/ApiV2';
import { checkReplaceProductImageHost } from 'helpers/utils';
import IMerchProductOrderItem from 'models/restful/IMerchProductOrderItem';
import React, { useState } from 'react';
import DesignActions from './components/DesignActions';
import DesignPricing from './components/DesignPricing';
import ProductVariationSelector from './components/ProductVariationSelector';
import UploadSection from './components/UploadSection';
import './Design.scss';
import { useQuery } from '@tanstack/react-query';

interface IOrderProps {
  orderItem: IMerchProductOrderItem;
  showRequired: boolean;
  updateOrderFromServer(): void;
}

const Design: React.FunctionComponent<IOrderProps> = ({
  orderItem,
  showRequired,
  updateOrderFromServer
}: IOrderProps) => {
  const { data: { products } = { products: [] } } = useQuery({
    queryKey: ['shop-data'],
    queryFn: () => apiV2.category.getShopData(),
    staleTime: 5 * 60 * 1000
  });

  const product = products.find(p => p.ID === orderItem.ProductID);
  const [key, setKey] = useState(0);

  const [mockupUploadProgress, setMockupUploadProgress] = useState<number>(0);
  const [mockupUploading, setMockupUploading] = useState<boolean>(false);
  const [artworkUploadProgress, setArtworkUploadProgress] = useState<number>(0);
  const [artworkUploading, setArtworkUploading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const uploadFile = (name: string, file: File) => {
    if (name === 'Mockup') setMockupUploading(true);
    if (name === 'Artwork') setArtworkUploading(true);
    apiV2.file
      .relationFileUpload({
        dataObjectName: 'MerchProductOrderItem',
        id: orderItem.ID,
        relationName: name,
        files: [file],
        cfg: {
          onUploadProgress: progressEvent => {
            if (name === 'Mockup')
              setMockupUploadProgress(progressEvent.progress as number);
            if (name === 'Artwork')
              setArtworkUploadProgress(progressEvent.progress as number);
          }
        }
      })
      .then(resp => {
        if (name === 'Mockup') {
          orderItem.Mockup = resp.item;
          orderItem.MockupID = orderItem.Mockup.ID;
        }
        if (name === 'Artwork') {
          orderItem.Artwork = resp.item;
          orderItem.ArtworkID = orderItem.Artwork.ID;
        }
        if (name === 'Mockup') setMockupUploading(false);
        if (name === 'Artwork') setArtworkUploading(false);
      });
  };

  const updateOI = (orderItem: IMerchProductOrderItem, updateState = false) => {
    setDisabled(true);
    apiV2.crud
      .update({
        dataObjectName: 'MerchProductOrderItem',
        dataObject: orderItem
      })
      .then(() => {
        if (updateState) updateOrderFromServer();
        else setKey(key + 1);
      })
      .finally(() => setDisabled(false));
  };

  return (
    <div className="design-card needs-validation" key={key}>
      <div className="design-card__content">
        <div className="design-card__preview">
          <div className="product-header__info">
            <h3 className="product-header__title">{product?.Title}</h3>
          </div>
          <div className="product-preview">
            <div className="product-preview__image">
              <img
                src={checkReplaceProductImageHost(
                  product?._props.previewImageUrl
                )}
                alt={product?.Title}
                className="product-preview__img"
              />
            </div>
            <div className="product-preview__details">
              <p className="lh-xs line-clamp-3 product-preview__description">
                {product?.Description}
              </p>
            </div>
          </div>
        </div>

        <div className="design-card__options">
          {product && (
            <ProductVariationSelector
              product={product}
              orderItem={orderItem}
              showRequired={showRequired}
              onVariationChange={productJson => {
                orderItem.ProductJSON = productJson;
                updateOI(orderItem, true);
              }}
            />
          )}

          <UploadSection
            orderItem={orderItem}
            showRequired={showRequired}
            onUpload={uploadFile}
            mockupUploading={mockupUploading}
            artworkUploading={artworkUploading}
            mockupUploadProgress={mockupUploadProgress}
            artworkUploadProgress={artworkUploadProgress}
          />

          <DesignPricing
            product={product}
            orderItem={orderItem}
            onQuantityChange={quantity => {
              orderItem.Quantity = quantity;
              updateOI(orderItem, true);
            }}
            disabled={disabled}
          />
        </div>
      </div>

      <DesignActions orderItem={orderItem} onUpdate={updateOrderFromServer} />
    </div>
  );
};

export default Design;
